// SINGLETON: Only this file does logging. No console.log anywhere else in src/.
import * as Sentry from '@sentry/react'
import { config } from './config'

type LogContext = Record<string, unknown>

function formatMessage(level: string, message: string, context?: LogContext): string {
  return JSON.stringify({ level, message, timestamp: new Date().toISOString(), ...context })
}

export const logger = {
  debug: (message: string, context?: LogContext): void => {
    if (config.isDev) {
      // eslint-disable-next-line no-console
      console.debug(formatMessage('debug', message, context))
    }
  },

  info: (message: string, context?: LogContext): void => {
    if (config.isDev) {
      // eslint-disable-next-line no-console
      console.info(formatMessage('info', message, context))
    }
  },

  warn: (message: string, context?: LogContext): void => {
    // In prod with Sentry configured, route to Sentry only (no console noise).
    // In dev or when Sentry is absent, fall back to console so nothing is silently swallowed.
    if (config.isDev || !config.sentryDsn) {
      // eslint-disable-next-line no-console
      console.warn(formatMessage('warn', message, context))
    }
    if (config.sentryDsn) {
      Sentry.captureMessage(message, { level: 'warning', extra: context as Record<string, unknown> })
    }
  },

  error: (message: string, error?: Error, context?: LogContext): void => {
    if (config.isDev || !config.sentryDsn) {
      // eslint-disable-next-line no-console
      console.error(formatMessage('error', message, { error: error?.message, ...context }))
    }
    if (config.sentryDsn) {
      if (error) {
        Sentry.captureException(error, { extra: { message, ...context } as Record<string, unknown> })
      } else {
        Sentry.captureMessage(message, { level: 'error', extra: context as Record<string, unknown> })
      }
    }
  },
}
